import React, { useEffect, useState } from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Tooltip
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { read } from 'xlsx';

import {
  MDTypography,
  MDBox,
  MDDropzone,
  MDButton,
  FaIcon,
  CustomSelect,
  CloseButton
} from '@components/index';

import useUploadProject from '@hooks/useUploadProject';
import { sendGAData } from '@utils/googleAnalytics';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CLICKED_CONNECT_DATA,
  CLICKED_READ_CLIPBOARD,
  ERROR_API_FILE_SIZE_LIMIT,
  ERROR_UNSUPPORTED_FORMAT,
  PAGE_VISITED,
  SELECTED_A_SHEET,
  UPLOADED_FILE
} from '@constants/eventNames';
import { getPageName, roundDecimal } from '@utils/common';
import { CLICKED_KNOW_MORE } from '@constants/eventNames';
import { RootState } from '@store/store';

import useUserDetails from '@hooks/useUserDetails';

import LinearProgressIndicator from '@pages/MyProjects/LinearProgressIndicator';
import { PricingPlans } from '@constants/pricing';
import UploadSpreadsheet from './UploadSpreadsheet';
import { useRedirectionMutation } from '@api/redirection';
import useToast from '@hooks/useToast';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { FeatureTypes } from '@constants/common';

enum FileUploadTypes {
  DRAG_UPLOAD,
  API,
  G_SHEET_UPLOAD
}

type DisabledUploadTypes = {
  [key: string]: FileUploadTypes[];
};

const disabledUploadTypes: DisabledUploadTypes = {
  CSV: [],
  EXCEL: [],
  GSHEET: [FileUploadTypes.DRAG_UPLOAD, FileUploadTypes.API],
  API: [FileUploadTypes.DRAG_UPLOAD, FileUploadTypes.G_SHEET_UPLOAD]
};

interface IFileUploadContentProps {
  handleFileUploadSuccess: any;
  onClose: any;
  isAddData: boolean;
  startUpload: boolean;
  uploadSpreadsheetPayload: {
    file_name: string;
    sheet_link?: string;
    sheet_name: string;
    sheet_id?: string;
    project_id?: string;
    xlsx_id?: string;
  };
  handleGoogleSheetUpload: (spreadsheetLink: string) => Promise<void>;
  setIsValidSpreadsheet: React.Dispatch<React.SetStateAction<boolean>>;
  setSpreadsheetLink: React.Dispatch<React.SetStateAction<string>>;
  onGetCombineDetails: (arg: string) => void;
  initFileType: 'CSV' | 'EXCEL' | 'API' | 'GSHEET';
}

const FileUploadContent: React.FC<IFileUploadContentProps> = ({
  handleFileUploadSuccess,
  onClose,
  isAddData,
  startUpload,
  uploadSpreadsheetPayload,
  handleGoogleSheetUpload,
  setIsValidSpreadsheet,
  setSpreadsheetLink,
  onGetCombineDetails,
  initFileType
}) => {
  const { getUserDetails } = useUserDetails();
  const { pushInfoToast } = useToast();

  const userId = useSelector(
    (state: RootState) => state?.userDetails?.user?.id
  );
  const projectId = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData?.id
  );
  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const isBetaUser = userDetails?.user.is_beta;

  const isProPlusUser = userDetails?.plan === PricingPlans.PRO_PLUS;

  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );

  const isConnectDataEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.restApi
  );

  const redirectUrl = process.env.REACT_APP_REDIRECT_URL;

  const disabledTypes = disabledUploadTypes[initFileType] || [];

  const {
    userUploadProject,
    userUploadSpreadsheet,
    isUploading,
    isUploadError,
    isUploadSuccess,
    uploadPercent,
    uploadSpreadsheet
  } = useUploadProject();

  const [redirectToBubble, { isLoading }] = useRedirectionMutation();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const [file, setFile] = useState<File>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isUploadable, setIsUploadable] = useState(true);
  const [showClipboardTooltip, setShowClipboardTooltip] = useState(false);
  const [sheetNames, setSheetNames] = useState<any[] | []>([]);
  const [openSheetSelectionModal, setOpenSheetSelectionModal] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState('');

  const uploadDataFile = async (file: any, sheetName?: string) => {
    if (file) {
      const response = await userUploadProject(
        file,
        file.size,
        isAddData ? projectId : undefined,
        sheetName ? sheetName : ''
      );

      setErrorMessage(response?.data?.msg);

      if (isAddData && response?.file_id)
        onGetCombineDetails(response?.file_id);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];

    if (
      file?.type != 'text/csv' &&
      file?.type != 'application/vnd.ms-excel' &&
      file?.type !=
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setIsUploadable(false);
      sendAmplitudeData(ERROR_UNSUPPORTED_FORMAT, { file_type: file?.type });
      setErrorMessage(
        'We only support .csv and .xls format file at this moment. Request you to re-upload file.'
      );

      return;
    }

    if (file?.size >= 100000000 && isProPlusUser) {
      setIsUploadable(false);
      sendAmplitudeData(ERROR_API_FILE_SIZE_LIMIT, {
        file_size: getFileSize(file.size)
      });
      setErrorMessage(
        `Your file is more than 100MB. Please re-upload a smaller file.`
      );

      return;
    }
    const fileSizeInKB = file.size / 1024;
    const fileSizeInMB = fileSizeInKB / 1024;

    sendAmplitudeData(UPLOADED_FILE, {
      'File Size':
        fileSizeInMB >= 1
          ? fileSizeInMB.toFixed(2) + 'MB'
          : fileSizeInKB.toFixed(2) + 'KB',
      file_type: file?.type,
      at_stage: isAddData ? 'combine' : 'new project'
    });

    setFile(file);
    setIsUploadable(true);

    if (file?.type !== 'text/csv') {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const workbook = read(reader.result, { type: 'binary' });
          const sheetNames = workbook.SheetNames;

          setSheetNames(
            sheetNames.map((sheet: string) => ({
              value: sheet,
              label: sheet,
              disbled: false
            }))
          );

          if (sheetNames.length > 1) {
            setOpenSheetSelectionModal(true);
            setSelectedSheet(sheetNames[0]);
          } else {
            uploadDataFile(file);
          }
        } catch (error) {
          uploadDataFile(file);
        }
      };

      reader.readAsBinaryString(file);
    } else {
      uploadDataFile(file);
    }
  };

  useEffect(() => {
    const uploadSpreadsheet = async () => {
      try {
        const response = await userUploadSpreadsheet(uploadSpreadsheetPayload);

        if (isAddData && response?.file_id)
          onGetCombineDetails(response?.file_id);
      } catch (error: any) {
        if (
          error.status === 403 &&
          error.data === 'This google sheet is already connected to the project'
        )
          pushInfoToast(
            <>
              Looks like you are choosing the same sheet as in this project. We
              recommend that you:
              <br /> - Select “Update data” button if you wish to update the
              same sheet. <br />- Choose another sheet if you wish to combine a
              different sheet.
            </>
          );
      }
    };

    if (startUpload) uploadSpreadsheet();
  }, [startUpload]);

  useEffect(() => {
    sendAmplitudeData(PAGE_VISITED, {
      'Page Name': 'Upload Modal',
      Navigation: getPageName()
    });
    sendGAData(PAGE_VISITED, {
      'Page Name': 'Upload Modal',
      Navigation: getPageName()
    });

    return () => {
      // Delayed update user details since upload count takes time to reflect in backend
      setTimeout(() => getUserDetails(userId), 5000);
    };
  }, []);

  // Fetch user projet upload data on success upload
  useEffect(() => {
    if (isUploadSuccess) getUserDetails(userId);
  }, [isUploadSuccess]);

  useEffect(() => {
    if (isUploadSuccess) {
      handleFileUploadSuccess(startUpload);

      if (!isAddData && !uploadSpreadsheet) onClose();
    }
  }, [isUploadSuccess, isAddData]);

  useEffect(() => {
    if (uploadPercent.loaded === 100 && uploadSpreadsheet)
      setTimeout(() => {
        onClose();
      }, 500);
  }, [uploadPercent]);

  useEffect(() => {
    if (isUploadError && uploadSpreadsheet) onClose();
  }, [isUploadError]);

  const getFileSize = (fileSize: number) => {
    const fileSizeInKB = fileSize / 1024;
    const fileSizeInMB = fileSizeInKB / 1024;

    if (fileSizeInMB >= 1) return `${roundDecimal(fileSizeInMB, 1)} MB`;
    else return `${roundDecimal(fileSizeInKB, 1)} KB`;
  };

  const fileUploadProgress = (uploadPercent.loaded * 100) / uploadPercent.total;

  const handleSheetSelection = async () => {
    if (file) {
      sendAmplitudeData(SELECTED_A_SHEET, {
        file_type: file?.type,
        sheet_name: selectedSheet
      });
      setOpenSheetSelectionModal(false);
      uploadDataFile(file, selectedSheet);
    }
  };

  const handleConnectButtonClick = async () => {
    if (subscriptionFlag) {
      sendAmplitudeData(CLICKED_CONNECT_DATA);
      const planResponse = await getFeatureAccess(FeatureTypes.REST_API);

      if (planResponse?.isSuccess) {
        const response = await redirectToBubble({
          redirect_to: 'account_manage_integrations',
          user_uuid: userDetails?.user?.user_uuid,
          user: userDetails?.user?.id,
          ...(userDetails?.user?.customer_uuid && {
            customer_uuid: userDetails?.user?.customer_uuid
          })
        });

        if ((response as any)?.data?.response?.redirect_to)
          window.open((response as any)?.data?.response?.redirect_to, '_blank');
      }
    }
  };

  return (
    <>
      <DialogTitle
        sx={{ pl: '20px', my: '10px' }}
        {...({ component: 'div' } as any)}
      >
        {(isUploadError || !isUploadable) && errorMessage && (
          <Alert
            severity='error'
            sx={{ my: '5px' }}
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        )}
        <MDBox display='flex' direction='row' alignItems='baseline'>
          <MDTypography variant='h3' color='secondary' textGradient>
            {isAddData ? 'Upload data to combine' : 'Upload, link or copy'}
          </MDTypography>
          {isAddData ? (
            <Tooltip
              title='Add only one file i.e. the file you want to add to this existing data/project.'
              placement='right'
            >
              <span>
                <FaIcon
                  icon='circle-question'
                  size='sm'
                  typographyProps={{
                    color: 'info'
                  }}
                  style={{
                    verticalAlign: 'middle',
                    marginLeft: '8px',
                    marginBottom: '6px'
                  }}
                />
              </span>
            </Tooltip>
          ) : (
            <></>
          )}
        </MDBox>
        <MDTypography variant='h5' color='info' sx={{ mt: '5px' }}>
          {isAddData
            ? 'Add a new file to your current file to combine'
            : 'Select your file, or copy from your clipboard'}
        </MDTypography>
      </DialogTitle>
      <DialogContent sx={{ pl: '20px', pr: '30px' }}>
        <MDTypography
          variant='h6'
          color='dark'
          sx={{ mb: '15px' }}
          opacity={
            initFileType
              ? disabledTypes.includes(FileUploadTypes.DRAG_UPLOAD)
                ? 0.5
                : 1
              : 1
          }
        >
          Drop or Select your file
        </MDTypography>
        {(((!file || isUploadError || openSheetSelectionModal) &&
          !uploadSpreadsheetPayload.sheet_name) ||
          (uploadSpreadsheetPayload.sheet_name && !uploadSpreadsheet)) && (
          <MDDropzone
            onFileSelect={onDrop}
            disabled={
              initFileType
                ? disabledTypes.includes(FileUploadTypes.DRAG_UPLOAD)
                : false
            }
          />
        )}
        <Grid>
          {((file && !isUploadError && !openSheetSelectionModal) ||
            uploadSpreadsheet) && (
            <Grid
              container
              sx={{
                height: '100px',
                border: 'solid 1px #D3D3D3',
                borderRadius: '10px',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px'
              }}
            >
              <Grid item>
                {isUploading || !isUploadSuccess ? (
                  <Grid
                    container
                    direction='column'
                    alignItems='center'
                    gap={0.5}
                  >
                    <MDTypography variant='sm' color='dark' fontSize={10}>
                      Uploaded {` : `}
                      {roundDecimal(fileUploadProgress || 0, 1)} %
                    </MDTypography>
                    <MDBox marginLeft='-25px'>
                      <LinearProgressIndicator
                        fileProgress={fileUploadProgress}
                      />
                    </MDBox>
                    {file && (
                      <MDTypography variant='sm' color='dark' fontSize={10}>
                        Size: {getFileSize(file.size)}
                      </MDTypography>
                    )}
                    <MDTypography
                      color='dark'
                      variant='sm'
                      fontSize={10}
                      sx={{ textAlign: 'center' }}
                    >
                      {file?.name ?? ''}
                    </MDTypography>
                  </Grid>
                ) : isUploadSuccess ? (
                  <MDBox
                    bgColor='secondary'
                    variant='gradient'
                    borderRadius='15px'
                    height='80px'
                    width='120px'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    alignContent='center'
                    flexDirection='column'
                    textAlign='center'
                  >
                    <FontAwesomeIcon icon='check' size='sm' color='white' />
                    <MDTypography color='white' pt={1} fontSize={8}>
                      {file?.name ?? ''}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox height='80px' width='120px'></MDBox>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>

        <UploadSpreadsheet
          startUpload={startUpload}
          setIsValidSpreadsheet={setIsValidSpreadsheet}
          setSpreadsheetLinkForUpload={setSpreadsheetLink}
          handleGoogleSheetUpload={handleGoogleSheetUpload}
          isDisabled={
            initFileType
              ? disabledTypes.includes(FileUploadTypes.G_SHEET_UPLOAD)
              : false
          }
        />
        <MDTypography
          variant='h6'
          color='dark'
          sx={{ mb: '15px' }}
          opacity={
            initFileType
              ? disabledTypes.includes(FileUploadTypes.API)
                ? 0.5
                : 1
              : 1
          }
        >
          Choose from hundreds of data sources and connect data
        </MDTypography>
        <Grid
          container
          direction='column'
          spacing={1}
          sx={{
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Grid item>
            <Grid container display='flex' flexDirection='row' gap={1}>
              <MDButton
                color='light'
                size='medium'
                onClick={handleConnectButtonClick}
                disabled={
                  !redirectUrl ||
                  isLoading ||
                  (initFileType
                    ? disabledTypes.includes(FileUploadTypes.API)
                    : false)
                }
              >
                {subscriptionFlag &&
                  (isConnectDataEnabledByPlan ? (
                    <FaIcon
                      icon='lock-open'
                      style={{ marginRight: '7px' }}
                      typographyProps={{
                        variant: 'xs',
                        color: 'primary',
                        mt: '-1.5px'
                      }}
                    />
                  ) : (
                    <FaIcon
                      icon='lock'
                      style={{ marginRight: '7px' }}
                      typographyProps={{
                        variant: 'xs',
                        color: 'primary',
                        mt: '-1.5px'
                      }}
                    />
                  ))}
                <FaIcon
                  typographyProps={{ variant: 'xs', color: 'primary' }}
                  icon='gear'
                />
                <MDTypography pl={1} variant='xs' color='info'>
                  LINK DATA SOURCE (API)
                </MDTypography>
                {isLoading && (
                  <CircularProgress
                    size={20}
                    style={{ color: 'primary', marginLeft: '10px' }}
                  />
                )}
              </MDButton>
            </Grid>
          </Grid>
        </Grid>
        {isBetaUser && (
          <MDTypography variant='h6' color='dark' sx={{ my: '15px' }}>
            Or let us read your Clipboard
          </MDTypography>
        )}
        <Grid
          container
          direction='column'
          sx={{ justifyContent: 'center', alignItems: 'center' }}
          mb={1}
        >
          {isBetaUser && (
            <Grid item sx={{ paddingTop: '8px !important' }}>
              <Tooltip
                open={showClipboardTooltip}
                title='Coming Soon'
                placement='right'
                sx={{ zIndex: '999 !important' }}
                onClose={() => setShowClipboardTooltip(false)}
              >
                <div>
                  <MDButton
                    size='medium'
                    color='light'
                    onClick={() => {
                      sendAmplitudeData(CLICKED_READ_CLIPBOARD);
                      setShowClipboardTooltip(true);
                    }}
                  >
                    <FaIcon
                      typographyProps={{ variant: 'xs', color: 'primary' }}
                      icon='glasses'
                    />
                    <MDTypography pl={1} variant='xs' color='info'>
                      READ CLIPBOARD
                    </MDTypography>
                  </MDButton>
                </div>
              </Tooltip>
            </Grid>
          )}
          <Grid item mt={2}>
            <span
              role='presentation'
              onClick={() =>
                isAddData
                  ? sendAmplitudeData(CLICKED_KNOW_MORE, {
                      'Feature Name': 'Combine Data'
                    })
                  : sendAmplitudeData(CLICKED_KNOW_MORE, {
                      'Feature Name': 'File Upload'
                    })
              }
            >
              <MDTypography
                sx={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  paddingY: isAddData ? '.6rem' : 0
                }}
                variant='sm'
                color='secondary'
                component={Link}
                underline
                href={
                  isAddData
                    ? 'https://www.notion.so/datasquirrel/What-does-Combine-Data-do-748d2f782e5c4cd99b64a5a66c67da3c?pvs=4'
                    : 'https://www.notion.so/Want-to-know-more-about-Importing-data-into-DataSquirrel-ea284f2ef9f142939a2802ada6ff11b0'
                }
                target='_blank'
                fontWeight='medium'
                textGradient
              >
                {isAddData
                  ? 'What does Combine Data do?'
                  : 'Know more about importing data into DataSquirrel'}
              </MDTypography>
            </span>
          </Grid>
        </Grid>
      </DialogContent>
      <Dialog
        open={openSheetSelectionModal}
        onClose={() => {
          setOpenSheetSelectionModal(false);
          onClose();
        }}
        sx={{
          '& .MuiPaper-root': {
            width: '540px',
            maxWidth: '800px',
            position: 'relative',
            overflow: 'visible'
          }
        }}
      >
        <CloseButton
          onClose={() => {
            setOpenSheetSelectionModal(false);
            onClose();
          }}
        />
        <DialogTitle sx={{ my: 2 }} {...({ component: 'div' } as any)}>
          <MDTypography
            variant='h3'
            color='secondary'
            textGradient
            sx={{ whiteSpace: 'wrap' }}
          >
            Select a sheet
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <Grid container gap='20px'>
            <MDTypography
              variant='h5'
              color='info'
              sx={{ overflowWrap: 'anywhere' }}
            >
              Your Dataset has multiple sheets. Select the one you would like to
              analyse.
            </MDTypography>
            <Grid
              container
              display='flex'
              justifyContent='space-between'
              mb={2}
            >
              <CustomSelect
                options={sheetNames}
                value={selectedSheet}
                sx={{ width: '80%' }}
                handleValueChange={(e: any) => {
                  setSelectedSheet(e.target.value);
                }}
              />
              <MDButton
                size='small'
                color='primary'
                variant='gradient'
                onClick={handleSheetSelection}
              >
                OK
              </MDButton>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FileUploadContent;
